/**
 * 중개사용 — 땅주인 매물 열람 페이지 (/owner-listings)
 * 인증된 중개사(is_verified: true)만 접근 가능
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "../../hooks/useIsMobile";
import { usePageTitle } from "../../hooks/usePageTitle";
import { AppHeader } from "../../components/AppHeader";
import { AppFooter } from "../../components/AppFooter";
import { apiFetch } from "../../utils/api";

export function OwnerListings() {
  usePageTitle("땅주인 직접 매물");
  const navigate = useNavigate();
  const m = useIsMobile();

  const [listings, setListings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [requesting, setRequesting] = useState<string | null>(null);
  const [requestDone, setRequestDone] = useState<Set<string>>(new Set());

  // 브로커 정보 확인
  const brokerInfo = (() => {
    try {
      const s = localStorage.getItem("broker_info");
      return s ? JSON.parse(s) : null;
    } catch { return null; }
  })();

  useEffect(() => {
    if (!localStorage.getItem("broker_token")) {
      navigate("/login", { replace: true });
      return;
    }
    apiFetch("/api/broker/owner-listings")
      .then((d: any) => setListings(d.listings || []))
      .catch((e: any) => setError(e.message || "불러오기 실패"))
      .finally(() => setLoading(false));
  }, [navigate]);

  const handleRequest = async (id: string) => {
    if (requesting || requestDone.has(id)) return;
    if (!window.confirm("이 매물에 연결 신청을 하시겠습니까?\n신청 후 땅주인에게 알림이 전송됩니다.")) return;

    setRequesting(id);
    try {
      await apiFetch(`/api/broker/owner-listings/${id}/request`, { method: "POST" });
      setRequestDone((prev) => new Set(prev).add(id));
      // 목록 갱신
      const d = await apiFetch("/api/broker/owner-listings") as any;
      setListings(d.listings || []);
    } catch (e: any) {
      alert(e.message || "신청에 실패했습니다");
    } finally {
      setRequesting(null);
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" as const, background: "var(--bg-primary)" }}>
      <AppHeader backTo="/listings" backLabel="\u2190 \uB9E4\uBB3C \uBAA9\uB85D" />
      <div style={{ flex: 1, maxWidth: 1000, margin: "0 auto", width: "100%", padding: m ? "20px 16px 40px" : "40px 32px 60px" }}>

        <div style={{ paddingBottom: 20, borderBottom: "1px solid var(--border-light)", marginBottom: 28 }}>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: "var(--text-primary)", margin: "0 0 6px" }}>
            {"🌱"} 땅주인 직접 매물
          </h1>
          <p style={{ fontSize: 14, color: "var(--text-muted)", margin: 0 }}>
            땅주인이 직접 등록한 토지 매물입니다. 연결 신청 후 정확한 주소와 연락처를 확인할 수 있습니다.
          </p>
        </div>

        {!brokerInfo?.is_verified && (
          <div style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", borderRadius: "var(--radius-md)", padding: "14px 18px", marginBottom: 20 }}>
            <p style={{ margin: 0, fontSize: 13, color: "#F87171", fontWeight: 600 }}>
              {"⚠️"} 자격증 인증된 중개사만 열람 가능합니다
            </p>
            <p style={{ margin: "4px 0 0", fontSize: 12, color: "var(--text-muted)" }}>
              마이페이지에서 자격증 서류를 제출하고 인증을 완료해 주세요.
            </p>
          </div>
        )}

        {loading ? (
          <p style={{ color: "var(--text-muted)", textAlign: "center" as const, padding: "40px 0" }}>불러오는 중...</p>
        ) : error ? (
          <div style={{ textAlign: "center" as const, padding: "40px 20px" }}>
            <p style={{ color: "#F87171", marginBottom: 16 }}>{error}</p>
            <button onClick={() => navigate("/mypage")} style={{ padding: "10px 22px", background: "linear-gradient(135deg,#B8972E,#D4AF37)", color: "#0F172A", border: "none", borderRadius: "var(--radius-md)", fontWeight: 700, cursor: "pointer" }}>
              인증 신청하러 가기
            </button>
          </div>
        ) : listings.length === 0 ? (
          <div style={{ textAlign: "center" as const, padding: "60px 20px" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>{"📭"}</div>
            <p style={{ color: "var(--text-muted)" }}>현재 등록된 땅주인 매물이 없습니다</p>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: m ? "1fr" : "repeat(auto-fill, minmax(400px, 1fr))", gap: 16 }}>
            {listings.map((l: any) => {
              const isMyMatch = !!l.address_full;
              const isDone = requestDone.has(l.id) || l.status === "matched";

              return (
                <div key={l.id} style={{
                  background: "var(--bg-card)",
                  border: "1px solid var(--border-light)",
                  borderLeft: "3px solid " + (isMyMatch ? "#10B981" : "var(--lt-gold)"),
                  borderRadius: "var(--radius-xl)",
                  padding: "20px",
                  boxShadow: "var(--shadow-sm)",
                  display: "flex",
                  flexDirection: "column" as const,
                  gap: 12,
                }}>
                  {/* 헤더 */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" as const, gap: 8 }}>
                    <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "var(--text-primary)" }}>
                      {isMyMatch ? l.address_full : l.address_masked}
                    </p>
                    {isMyMatch && (
                      <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: "rgba(16,185,129,0.12)", color: "#10B981" }}>
                        {"✓"} 내가 신청
                      </span>
                    )}
                  </div>

                  {/* 정보 그리드 */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 16px" }}>
                    <div>
                      <p style={{ margin: 0, fontSize: 11, color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase" as const, letterSpacing: "0.5px" }}>지목</p>
                      <p style={{ margin: "2px 0 0", fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>{l.land_type}</p>
                    </div>
                    <div>
                      <p style={{ margin: 0, fontSize: 11, color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase" as const, letterSpacing: "0.5px" }}>면적</p>
                      <p style={{ margin: "2px 0 0", fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>{(l.area_sqm || 0).toLocaleString()}㎡</p>
                    </div>
                    <div>
                      <p style={{ margin: 0, fontSize: 11, color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase" as const, letterSpacing: "0.5px" }}>희망가</p>
                      <p style={{ margin: "2px 0 0", fontSize: 15, fontWeight: 700, color: "#FBBF24" }}>{(l.asking_price || 0).toLocaleString()}만원</p>
                    </div>
                    <div>
                      <p style={{ margin: 0, fontSize: 11, color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase" as const, letterSpacing: "0.5px" }}>평당가</p>
                      <p style={{ margin: "2px 0 0", fontSize: 13, color: "var(--text-secondary)" }}>
                        {l.area_sqm > 0 ? `${Math.round(l.asking_price / (l.area_sqm / 3.305)).toLocaleString()}만원` : "-"}
                      </p>
                    </div>
                  </div>

                  {/* 특이사항 */}
                  {l.notes && (
                    <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)", lineHeight: 1.6, padding: "10px 12px", background: "var(--bg-secondary)", borderRadius: "var(--radius-sm)" }}>
                      {l.notes}
                    </p>
                  )}

                  {/* 매칭 후 공개 정보 */}
                  {isMyMatch && (
                    <div style={{ padding: "12px", background: "rgba(16,185,129,0.06)", border: "1px solid rgba(16,185,129,0.2)", borderRadius: "var(--radius-md)" }}>
                      <p style={{ margin: "0 0 4px", fontSize: 12, fontWeight: 700, color: "#10B981" }}>{"🔓"} 연결 정보</p>
                      <p style={{ margin: "0 0 2px", fontSize: 13, color: "var(--text-secondary)" }}>{"📍"} {l.address_full}</p>
                      <p style={{ margin: "0 0 2px", fontSize: 13, color: "var(--text-secondary)" }}>{"📞"} {l.owner_phone}</p>
                      {l.contact_hours && <p style={{ margin: 0, fontSize: 12, color: "var(--text-muted)" }}>연락가능: {l.contact_hours}</p>}
                    </div>
                  )}

                  {/* 연결 신청 버튼 */}
                  {!isMyMatch && (
                    <button
                      onClick={() => handleRequest(l.id)}
                      disabled={!!requesting || isDone || l.status === "matched"}
                      style={{
                        padding: "11px",
                        borderRadius: "var(--radius-md)",
                        border: "none",
                        background: isDone || l.status === "matched"
                          ? "rgba(100,116,139,0.15)"
                          : "linear-gradient(135deg,#B8972E,#D4AF37)",
                        color: isDone || l.status === "matched" ? "#64748B" : "#0F172A",
                        fontSize: 14,
                        fontWeight: 700,
                        cursor: isDone || l.status === "matched" ? "default" : "pointer",
                        marginTop: 4,
                      }}
                    >
                      {l.status === "matched" ? "이미 매칭됨" : isDone ? "신청 완료" : requesting === l.id ? "신청 중..." : "\uD83E\uDD1D 연결 신청하기"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <AppFooter />
    </div>
  );
}
